import api from './api';

export type DeliveryCourierVehicleType = 'MOTO' | 'CARRO' | 'BICICLETA' | 'OUTRO';

export interface DeliveryCourier {
  id: string;
  cnpj: string;
  name: string;
  phone: string | null;
  document: string | null;
  vehicleType: DeliveryCourierVehicleType | null;
  vehiclePlate: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListDeliveryCouriersParams {
  searchTerm?: string;
  isActive?: boolean;
  cnpj?: string;
}

export interface CreateDeliveryCourierPayload {
  name: string;
  phone?: string | null;
  document?: string | null;
  vehicleType?: DeliveryCourierVehicleType | null;
  vehiclePlate?: string | null;
  notes?: string | null;
  isActive?: boolean;
  cnpj?: string;
}

export interface UpdateDeliveryCourierPayload {
  name?: string;
  phone?: string | null;
  document?: string | null;
  vehicleType?: DeliveryCourierVehicleType | null;
  vehiclePlate?: string | null;
  notes?: string | null;
  isActive?: boolean;
  cnpj?: string;
}

const toIsoString = (value: Date | string | undefined): string => {
  if (!value) {
    return new Date().toISOString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
};

const sanitizeDigits = (value: string | null | undefined): string | null | undefined => {
  if (value === null) {
    return null;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const digits = value.replace(/\D/g, '');
  return digits.length > 0 ? digits : null;
};

const sanitizeText = (value: string | null | undefined): string | null | undefined => {
  if (value === null) {
    return null;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const normalizeCourier = (data: any): DeliveryCourier => ({
  id: String(data?.id ?? ''),
  cnpj: String(data?.cnpj ?? ''),
  name: String(data?.name ?? ''),
  phone: data?.phone ? String(data.phone) : null,
  document: data?.document ? String(data.document) : null,
  vehicleType: data?.vehicleType ? String(data.vehicleType).toUpperCase() as DeliveryCourierVehicleType : null,
  vehiclePlate: data?.vehiclePlate ? String(data.vehiclePlate).toUpperCase() : null,
  notes: data?.notes ? String(data.notes) : null,
  isActive: Boolean(data?.isActive ?? true),
  createdAt: toIsoString(data?.createdAt),
  updatedAt: toIsoString(data?.updatedAt),
});

const buildRequestBody = (payload: {
  name?: string;
  phone?: string | null;
  document?: string | null;
  vehicleType?: DeliveryCourierVehicleType | null;
  vehiclePlate?: string | null;
  notes?: string | null;
  isActive?: boolean;
}): Record<string, any> => {
  const body: Record<string, any> = {};

  if (payload.name !== undefined) {
    const trimmed = payload.name.trim();
    if (trimmed) {
      body.name = trimmed;
    }
  }

  const phone = sanitizeDigits(payload.phone);
  if (phone !== undefined) {
    body.phone = phone;
  }

  const document = sanitizeDigits(payload.document);
  if (document !== undefined) {
    body.document = document;
  }

  const vehicleType = payload.vehicleType === null ? null : payload.vehicleType?.toUpperCase();
  if (vehicleType !== undefined) {
    body.vehicleType = vehicleType;
  }

  const vehiclePlate = sanitizeText(payload.vehiclePlate);
  if (vehiclePlate !== undefined) {
    body.vehiclePlate = vehiclePlate ? vehiclePlate.toUpperCase() : vehiclePlate;
  }

  const notes = sanitizeText(payload.notes);
  if (notes !== undefined) {
    body.notes = notes;
  }

  if (payload.isActive !== undefined) {
    body.isActive = payload.isActive;
  }

  return body;
};

export const deliveryCourierService = {
  async list(params: ListDeliveryCouriersParams = {}): Promise<DeliveryCourier[]> {
    const response = await api.get('/admin/delivery/couriers', {
      params: {
        searchTerm: params.searchTerm?.trim() || undefined,
        isActive: params.isActive,
        cnpj: params.cnpj?.trim() || undefined,
      },
    });

    const payload = Array.isArray(response.data) ? response.data : [];
    return payload.map(normalizeCourier);
  },

  async get(id: string, cnpj?: string): Promise<DeliveryCourier> {
    const response = await api.get(`/admin/delivery/couriers/${id}`, {
      params: {
        cnpj: cnpj?.trim() || undefined,
      },
    });

    return normalizeCourier(response.data);
  },

  async create(payload: CreateDeliveryCourierPayload): Promise<DeliveryCourier> {
    const response = await api.post(
      '/admin/delivery/couriers',
      {
        ...buildRequestBody(payload),
      },
      {
        params: {
          cnpj: payload.cnpj?.trim() || undefined,
        },
      },
    );

    return normalizeCourier(response.data);
  },

  async update(id: string, payload: UpdateDeliveryCourierPayload): Promise<DeliveryCourier> {
    const response = await api.put(
      `/admin/delivery/couriers/${id}`,
      buildRequestBody(payload),
      {
        params: {
          cnpj: payload.cnpj?.trim() || undefined,
        },
      },
    );

    return normalizeCourier(response.data);
  },

  async delete(id: string, cnpj?: string): Promise<void> {
    await api.delete(`/admin/delivery/couriers/${id}`, {
      params: {
        cnpj: cnpj?.trim() || undefined,
      },
    });
  },
};

export default deliveryCourierService;
